import type { NetworkId, NetworkInfo } from "@alch/dx-entities";
import { COMPOSER_API_KEY, TRACE_COMPOSER_API_KEY } from "@util/constants";
import { IS_PRODUCTION } from "@util/deployEnv";
import { stringify } from "qs";
import type { ComposerMethodConfig } from "./methods.js";
import { getUniqueMethodConfig } from "./methods.js";
import type { SerializableComposerFormState } from "./sharing.js";
import {
  buildShareUrl,
  nullifyEmptyValues,
  parseStateFromShareUrl,
} from "./sharing.js";

export const getComposerUrlFromRequest = <Request extends { method?: string }>({
  methodConfigs,
  baseUrl,
  network,
  request,
}: {
  methodConfigs: ComposerMethodConfig[];
  baseUrl: string;
  network: NetworkId;
  request: Request;
}) => {
  const methodConfig = getUniqueMethodConfig(methodConfigs, {
    method: String(request.method),
    network,
  });

  if (!methodConfig) return null;

  const shareState = {
    network,
    method: methodConfig.name,
    body: request,
  } as SerializableComposerFormState;
  const shareUrl = buildShareUrl({ baseUrl, state: shareState });
  const shareUrlQuery = shareUrl.split("?")[1] || "";

  const state = parseStateFromShareUrl({
    search: shareUrlQuery,
    methodConfigs,
  });

  if (!state) return null;

  return shareUrl;
};

export const buildComposerRequestUrl = ({
  methodConfig,
  query,
  networkInfo,
  apiKey,
}: {
  methodConfig: ComposerMethodConfig;
  query: unknown | undefined;
  networkInfo: NetworkInfo | undefined;
  apiKey: string | undefined;
}) => {
  let url =
    networkInfo && "rpcUrl" in networkInfo // A temporary hack to use the rpcUrl for the request for V_DEVNET
      ? `${String(networkInfo.rpcUrl)}/{apiKey}`
      : methodConfig.url;

  const isTraceMethod = methodConfig.name.includes("trace");
  const defaultApiKey = isTraceMethod
    ? TRACE_COMPOSER_API_KEY
    : COMPOSER_API_KEY;

  url = url.replace("{apiKey}", apiKey || defaultApiKey);

  if (networkInfo) {
    url = url.replace("{network}", networkInfo.kebabCaseId);
  }

  if (!IS_PRODUCTION && !url.includes(defaultApiKey)) {
    url = url.replace("alchemy.com", "alchemypreview.com");
  }

  const queryString = query
    ? stringify(nullifyEmptyValues(query), {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: "repeat",
      })
    : "";

  return `${url}${queryString}`;
};
